import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, MapPin } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { sanitizeFormData, sanitizeInput, validateEmail, validatePhone, validateZipCode } from '../utils/security';

const Profile = ({ onOpenRegionSelector }) => {
  const navigate = useNavigate();
  const { currency } = useCurrency();
  const { userDoc, updateUser, logout } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
  });

  useEffect(() => {
    const saved = userDoc?.profile;
    if (!saved || typeof saved !== 'object') return;
    setFormData((prev) => ({
      ...prev,
      ...saved,
    }));
  }, [userDoc?.profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const errors = useMemo(() => {
    const email = String(formData.email || '').trim();
    const phone = String(formData.phone || '').trim();
    const zip = String(formData.zipCode || '').trim();

    return {
      email: email && !validateEmail(email) ? 'Enter a valid email.' : '',
      phone: phone && !validatePhone(phone) ? 'Enter a valid phone number.' : '',
      zipCode: zip && !validateZipCode(zip) ? 'Enter a valid ZIP/postal code.' : '',
    };
  }, [formData.email, formData.phone, formData.zipCode]);

  const onSave = async (e) => {
    e.preventDefault();

    const email = String(formData.email || '').trim();
    if (!email || !validateEmail(email)) {
      toast({ title: 'Invalid email', description: 'Please enter a valid email address.' });
      return;
    }

    if (errors.phone) {
      toast({ title: 'Invalid phone', description: 'Please enter a valid phone number.' });
      return;
    }

    if (errors.zipCode) {
      toast({ title: 'Invalid ZIP code', description: 'Please enter a valid ZIP/postal code.' });
      return;
    }

    const sanitized = sanitizeFormData(formData);
    if (typeof updateUser === 'function') {
      await updateUser({ profile: sanitized, email: sanitized.email });
    }

    toast({ title: 'Profile saved', description: 'Your details were saved for faster checkout.' });
  };

  const onSignOut = async () => {
    if (typeof logout !== 'function') return;
    try {
      await logout();
    } catch {
      // ignore
    }
    toast({ title: 'Signed out', description: 'You have been signed out.' });
    navigate('/');
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <h1 className="text-2xl font-medium tracking-wide">PROFILE</h1>
          <p className="text-sm text-gray-600 mt-2">
            Save your details for faster checkout.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/orders')}
          className="text-sm underline hover:opacity-60"
        >
          My Orders
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.06)] overflow-hidden">
        <div className="p-6 border-b border-gray-200 flex items-center gap-3">
          <User size={18} />
          <div className="text-sm font-medium tracking-wide">CUSTOMER DETAILS</div>
        </div>

        <form onSubmit={onSave} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest text-gray-600">EMAIL</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={`mt-2 w-full border px-4 py-3 text-sm focus:outline-none transition-colors bg-white ${
                  errors.email ? 'border-red-500 focus:border-red-600' : 'border-gray-300 focus:border-black'
                }`}
              />
              {errors.email ? <div className="mt-2 text-xs text-red-600">{errors.email}</div> : null}
            </div>

            <div>
              <label className="block text-xs tracking-widest text-gray-600">PHONE</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`mt-2 w-full border px-4 py-3 text-sm focus:outline-none transition-colors bg-white ${
                  errors.phone ? 'border-red-500 focus:border-red-600' : 'border-gray-300 focus:border-black'
                }`}
              />
              {errors.phone ? <div className="mt-2 text-xs text-red-600">{errors.phone}</div> : null}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest text-gray-600">FIRST NAME</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="mt-2 w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white"
              />
            </div>

            <div>
              <label className="block text-xs tracking-widest text-gray-600">LAST NAME</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="mt-2 w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest text-gray-600">ADDRESS</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="mt-2 w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white"
            />
          </div>

          <div>
            <label className="block text-xs tracking-widest text-gray-600">APARTMENT</label>
            <input
              type="text"
              name="apartment"
              value={formData.apartment}
              onChange={handleChange}
              className="mt-2 w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs tracking-widest text-gray-600">CITY</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className="mt-2 w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white"
              />
            </div>

            <div>
              <label className="block text-xs tracking-widest text-gray-600">STATE</label>
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="mt-2 w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white"
              />
            </div>

            <div>
              <label className="block text-xs tracking-widest text-gray-600">ZIP CODE</label>
              <input
                type="text"
                name="zipCode"
                value={formData.zipCode}
                onChange={handleChange}
                className={`mt-2 w-full border px-4 py-3 text-sm focus:outline-none transition-colors bg-white ${
                  errors.zipCode ? 'border-red-500 focus:border-red-600' : 'border-gray-300 focus:border-black'
                }`}
              />
              {errors.zipCode ? <div className="mt-2 text-xs text-red-600">{errors.zipCode}</div> : null}
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest text-gray-600">COUNTRY</label>
            <select
              name="country"
              value={formData.country}
              onChange={handleChange}
              className="mt-2 w-full border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors bg-white"
            >
              <option value="United States">United States</option>
              <option value="Canada">Canada</option>
              <option value="United Kingdom">United Kingdom</option>
              <option value="India">India</option>
              <option value="Australia">Australia</option>
              <option value="Japan">Japan</option>
              <option value="Germany">Germany</option>
              <option value="France">France</option>
              <option value="Spain">Spain</option>
              <option value="Italy">Italy</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="pt-2 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <button
              type="button"
              onClick={() => {
                if (typeof onOpenRegionSelector === 'function') onOpenRegionSelector();
              }}
              className="inline-flex items-center justify-center gap-2 border border-gray-300 px-4 py-3 text-sm font-medium tracking-wide hover:bg-gray-50 transition-colors"
            >
              <MapPin size={16} />
              REGION & CURRENCY ({currency})
            </button>

            <button
              type="submit"
              className="bg-black text-white px-5 py-3 text-sm font-medium tracking-wide hover:bg-gray-800 transition-colors"
            >
              SAVE PROFILE
            </button>
          </div>

          <div className="pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onSignOut}
              className="w-full border border-black py-3 text-sm font-medium tracking-wide hover:bg-black hover:text-white transition-colors"
            >
              SIGN OUT
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
